import { useEffect, useRef, useCallback } from 'react';
import { loadUserBindings, findActionForEvent, type HotKeyBindings } from './hotkeys';
import { useUIStore } from '../../stores/uiStore';
import { STUDY_ACTIONS } from '../worklist/studyActions';
import { pushError } from '../../stores/notificationStore';

/**
 * Global keyboard shortcut listener — mirrors DefaultHotKeysListener.
 *
 * Attaches to `document` keydown. Dispatches worklist actions when
 * no input element is focused. Viewer shortcuts are forwarded to
 * the viewer component when it's active.
 */
export function useHotKeys() {
  const bindingsRef = useRef<HotKeyBindings>(loadUserBindings());
  const selectedStudies = useUIStore((s) => s.selectedStudies);
  const selectedStudiesRef = useRef(selectedStudies);

  const setPage = useUIStore((s) => s.setPage);
  const page = useUIStore((s) => s.page);
  const pageRef = useRef(page);
  const toggleSplitScreen = useUIStore((s) => s.toggleSplitScreen);

  // Sync refs in effects (not during render)
  useEffect(() => {
    selectedStudiesRef.current = selectedStudies;
  }, [selectedStudies]);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't intercept when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      // Hard-wired shortcuts (not user-configurable)
      if (event.ctrlKey && event.key === '\\') {
        event.preventDefault();
        toggleSplitScreen();
        return;
      }

      const actionId = findActionForEvent(event, bindingsRef.current);
      if (!actionId) return;

      // Prevent default browser behavior for matched shortcuts
      event.preventDefault();
      event.stopPropagation();

      // Dispatch action
      switch (actionId) {
        case 'REFRESH':
          // Handled by TanStack Query refetch — just invalidate
          window.dispatchEvent(new CustomEvent('brit:refresh'));
          break;

        case 'NEXT_PAGE_SHOW':
          setPage(pageRef.current + 1);
          break;

        case 'PREVIOUS_PAGE_SHOW':
          setPage(Math.max(0, pageRef.current - 1));
          break;

        case 'FULL_SCREEN_HIDE_SHOW':
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          } else {
            document.documentElement.requestFullscreen().catch(() => {});
          }
          break;

        default: {
          // Try to match to a StudyAction
          const studies = selectedStudiesRef.current;
          if (studies.length === 0) break;

          // Map hotkey action IDs to study action IDs
          const hotkeyToStudyAction: Record<string, string> = {
            COMPLETE_STUDY: 'COMPLETESTUDY',
            ASSIGN_STUDY: 'ASSIGNSTUDY',
            ASSIGN_TO_ME: 'ASSIGN_TO_ME',
            RELEASE_STUDY: 'RELEASESTUDY',
            HOLD_STUDY: 'HOLDSTUDY',
            UNHOLD_STUDY: 'UNHOLDSTUDY',
            URGENT_FINDINGS: 'MARKURGENT',
            IMPORTANT_FINDINGS: 'MARKIMPORTANT',
            FOLLOW_UP_RECOMMENDED: 'MARKFOLLOWUP',
            DISCORDANCE: 'DISCORDANCE',
            SHARE_STUDY: 'SHARESTUDY',
            SEND_STUDY_TO: 'SENDSTUDY',
            DOWNLOAD_STUDY: 'DOWNLOADSTUDY',
            ADD_REPORT: 'CREATEREPORT',
            SHOW_STUDY_NOTES: 'SHOWNOTES',
            CONSULTATION: 'CONSULTATION',
          };

          const studyActionId = hotkeyToStudyAction[actionId];
          if (studyActionId) {
            const studyAction = STUDY_ACTIONS.find((a) => a.id === studyActionId);
            if (studyAction?.backendReady) {
              studyAction.handler(studies).catch((e) => {
                pushError(`Hotkey "${actionId}" failed: ${e instanceof Error ? e.message : String(e)}`);
              });
            }
          }
          break;
        }
      }
    },
    [setPage, toggleSplitScreen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /** Reload bindings (e.g. after user edits preferences) */
  const reloadBindings = useCallback(() => {
    bindingsRef.current = loadUserBindings();
  }, []);

  return { reloadBindings };
}
